import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { withAuth } from "../../../components/HOC/withAuth";
import { getStatusAppearance } from "../../../config/statusColors";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import Pagination from "../../../components/BaseUI/Pagination";
import { FiTruck, FiCheck, FiEye, FiArrowLeft } from "react-icons/fi";

import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import { formatInstantDateShortTr } from "../../../utils/appTime";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const StockProducts = () => {
  const [devices, setDevices] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sort, setSort] = useState({
    sortBy: "serialNumber",
    sortOrder: "desc",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const { getToken, getUser } = useAuth();
  const navigate = useNavigate();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/stock-products",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  useTitle("Rovion MES - Stok");

  const splitAlphaNum = (s) => {
    const str = (s ?? "").toString();
    if (!str) return [];
    return str.match(/[A-Za-z]+|\\d+|[^A-Za-z\\d]+/g) || [];
  };

  // A -> Z, char-by-char, letters come before numbers
  const compareCharByCharAZ = (a, b) => {
    const ax = splitAlphaNum(a);
    const bx = splitAlphaNum(b);
    const n = Math.max(ax.length, bx.length);

    for (let i = 0; i < n; i++) {
      const as = ax[i] ?? "";
      const bs = bx[i] ?? "";
      if (!as && !bs) return 0;
      if (!as) return -1;
      if (!bs) return 1;

      const aIsDigits = /^\\d+$/.test(as);
      const bIsDigits = /^\\d+$/.test(bs);
      const aIsLetters = /^[A-Za-z]+$/.test(as);
      const bIsLetters = /^[A-Za-z]+$/.test(bs);

      const typeRank = (isLetters, isDigits) =>
        isLetters ? 0 : isDigits ? 1 : 2;
      const ar = typeRank(aIsLetters, aIsDigits);
      const br = typeRank(bIsLetters, bIsDigits);
      if (ar !== br) return ar - br;

      if (aIsDigits && bIsDigits) {
        const an = Number(as);
        const bn = Number(bs);
        if (an !== bn) return an - bn;
        if (as.length !== bs.length) return as.length - bs.length;
        if (as !== bs)
          return as.localeCompare(bs, "en", { sensitivity: "base" });
        continue;
      }

      const cmp = as.localeCompare(bs, "en", { sensitivity: "base" });
      if (cmp !== 0) return cmp;
    }
    return 0;
  };

  const sortedModels = useMemo(() => {
    return [...(models || [])].sort((a, b) => {
      const byCode = compareCharByCharAZ(a?.code || "", b?.code || "");
      if (byCode !== 0) return byCode;
      return compareCharByCharAZ(a?.name || "", b?.name || "");
    });
  }, [models]);

  const modelColumns = [
    { name: "Model Adı", dataType: "text", id: 0 },
    { name: "Model Kodu", dataType: "text", id: 1 },
    {
      name: "Ürün Sayısı",
      dataType: "number",
      id: 2,
      customSorter: (a, b) => (a.deviceCount || 0) - (b.deviceCount || 0),
    },
    { name: "İşlemler", id: 3 },
  ];

  const deviceColumns = [
    { name: "", dataType: "text", id: -1, sortable: false },
    { name: "Seri Numarası", dataType: "text", id: 0, sortKey: "serialNumber" },
    { name: "Durum", dataType: "text", id: 2, sortKey: "status" },
    {
      name: "Oluşturulma",
      dataType: "date",
      id: 3,
      sortKey: "createdAt",
      customSorter: (a, b) => {
        const ta = new Date(a?.createdAt).getTime();
        const tb = new Date(b?.createdAt).getTime();
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      },
    },
  ];

  const {
    visibleColumns: modelVisibleColumns,
    toggleColumnVisibility: toggleModelColumn,
    resetToDefault: resetModelColumns,
  } = useColumnVisibility("stock-models-list", modelColumns);
  const {
    visibleColumns: deviceVisibleColumns,
    toggleColumnVisibility: toggleDeviceColumn,
    resetToDefault: resetDeviceColumns,
  } = useColumnVisibility("stock-devices-list", deviceColumns);

  const breadcrumbs = [{ label: "Rovion", url: "" }, { label: "Stok" }];

  useEffect(() => {
    fetchModels();
  }, []);

  useEffect(() => {
    if (selectedModel) {
      fetchDevices();
    }
  }, [
    pagination.page,
    pagination.limit,
    selectedModel,
    searchQuery,
    sort.sortBy,
    sort.sortOrder,
  ]);

  const fetchModels = async () => {
    try {
      setModelsLoading(true);
      const response = await axios.get(`${API_URL}/api/device-models`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const activeModels = response.data.filter(
        (model) => model.status === "active",
      );

      // Fetch device counts for each model
      const modelsWithCounts = await Promise.all(
        activeModels.map(async (model) => {
          try {
            const devicesResponse = await axios.get(
              `${API_URL}/api/devices?status=stock&modelId=${model._id}&limit=1`,
              {
                headers: {
                  Authorization: `Bearer ${getToken()}`,
                },
              },
            );
            return {
              ...model,
              deviceCount: devicesResponse.data.pagination?.total || 0,
            };
          } catch (error) {
            return { ...model, deviceCount: 0 };
          }
        }),
      );

      const sorted = [...modelsWithCounts].sort((a, b) => {
        const byCode = compareCharByCharAZ(a?.code || "", b?.code || "");
        if (byCode !== 0) return byCode;
        return compareCharByCharAZ(a?.name || "", b?.name || "");
      });
      setModels(sorted);
    } catch (error) {
      console.error("Modeller getirilemedi:", error);
    } finally {
      setModelsLoading(false);
    }
  };

  const fetchDevices = async () => {
    if (!selectedModel) return;

    try {
      setLoading(true);
      let url = `${API_URL}/api/devices?page=${pagination.page}&limit=${pagination.limit}&status=stock&modelId=${selectedModel._id}`;

      if (searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }
      url += `&sortBy=${sort.sortBy}&sortOrder=${sort.sortOrder}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const devices = response.data.devices || [];
      setDevices(devices);

      if (response.data.pagination) {
        setPagination({
          page: response.data.pagination.page || pagination.page,
          limit: response.data.pagination.limit || pagination.limit,
          total: response.data.pagination.total || 0,
          pages:
            response.data.pagination.totalPages ||
            Math.ceil(
              (response.data.pagination.total || 0) /
                (response.data.pagination.limit || 10),
            ),
        });
      }
    } catch (error) {
      console.error("Stok ürünleri getirilemedi:", error);
      setToasts([
        {
          description: "Stok ürünleri yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      stock: "Stok",
      assembly: "Montaj",
      delivered: "Teslim Edildi",
    };
    return statusLabels[status] || status;
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const toggleDeviceSelection = (deviceId) => {
    setSelectedDevices((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) {
        next.delete(deviceId);
      } else {
        next.add(deviceId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedDevices.size === devices.length) {
      setSelectedDevices(new Set());
    } else {
      setSelectedDevices(new Set(devices.map((d) => d._id)));
    }
  };

  const handleRedirectToDelivery = () => {
    if (selectedDevices.size === 0) {
      setToasts([
        {
          description: "Lütfen en az bir ürün seçin",
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    // Store selected device IDs in localStorage
    const deviceIdsArray = Array.from(selectedDevices);
    localStorage.setItem(
      "preSelectedDevicesForDelivery",
      JSON.stringify(deviceIdsArray),
    );

    // Navigate to CreateDelivery page
    navigate("/deliveries/create");
  };

  const handleModelClick = (model) => {
    setSelectedModel(model);
    setSelectedDevices(new Set());
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleBackToModels = () => {
    setSelectedModel(null);
    setDevices([]);
    setSelectedDevices(new Set());
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const totalProductsCount = models.reduce(
    (sum, model) => sum + (model.deviceCount || 0),
    0,
  );

  return (
    <AppPage
      title={selectedModel ? `${selectedModel.name} - Stok` : "Stok"}
      breadcrumbs={
        selectedModel
          ? [...breadcrumbs, { label: selectedModel.name }]
          : breadcrumbs
      }
      actions={
        selectedModel ? (
          <div className="d-flex gap-2">
            {canWrite && selectedDevices.size > 0 && (
              <Button
                appearance="primary-animated"
                icon={<FiTruck />}
                onClick={handleRedirectToDelivery}
                disabled={selectedDevices.size === 0}
              >
                Teslimata Yönlendir ({selectedDevices.size})
              </Button>
            )}
            <Button
              appearance="primary-subtle"
              onClick={handleBackToModels}
              icon={<FiArrowLeft />}
            >
              Modellere Dön
            </Button>
          </div>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center gap-2"
              style={{
                padding: "8px 16px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  color: "#6c757d",
                  fontWeight: "500",
                }}
              >
                Toplam Ürün:
              </span>
              <span
                style={{
                  fontSize: "16px",
                  color: "#00b4f3",
                  fontWeight: "700",
                }}
              >
                {totalProductsCount}
              </span>
            </div>
          </div>
        )
      }
    >
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          {!selectedModel ? (
            <>
              <Table
                columns={modelColumns || []}
                visibleColumns={modelVisibleColumns}
                tableId="stock-models-list"
                loading={modelsLoading}
                search={true}
                hideFooter={true}
                viewOptions={
                  <ListViewOptionsPanel
                    columns={modelColumns}
                    visibleColumns={modelVisibleColumns}
                    onToggleColumn={toggleModelColumn}
                    onResetColumns={resetModelColumns}
                    showApply={false}
                    showReset={false}
                  />
                }
              >
                {sortedModels.map((model) => (
                  <Tr key={model._id} data={model}>
                    {modelVisibleColumns.includes(0) && <Td>{model.name}</Td>}
                    {modelVisibleColumns.includes(1) && <Td>{model.code}</Td>}
                    {modelVisibleColumns.includes(2) && (
                      <Td>
                        <span
                          style={{
                            backgroundColor: "#3B82F615",
                            color: "#3B82F6",
                            padding: "5px 10px",
                            borderRadius: "5px",
                            width: "fit-content",
                            fontWeight: "600",
                          }}
                        >
                          {model.deviceCount || 0} Adet
                        </span>
                      </Td>
                    )}
                    {modelVisibleColumns.includes(3) && (
                      <Td>
                        <div className="d-flex gap-2">
                          <FiEye
                            className="cursor-pointer"
                            style={{ fontSize: "20px", cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModelClick(model);
                            }}
                            title="Ürünleri Görüntüle"
                          />
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Table>
            </>
          ) : (
            <>
              <div className="stock-products-select-all">
                <label className="select-all-label">
                  <input
                    type="checkbox"
                    className="select-all-checkbox"
                    checked={
                      devices.length > 0 &&
                      selectedDevices.size === devices.length
                    }
                    onChange={toggleSelectAll}
                  />
                  <span className="custom-checkbox">
                    {devices.length > 0 &&
                      selectedDevices.size === devices.length && (
                        <FiCheck className="check-icon" />
                      )}
                  </span>
                  <span className="select-all-text">
                    Tümünü Seç{" "}
                    <span className="selection-count">
                      ({selectedDevices.size}/{devices.length})
                    </span>
                  </span>
                </label>
              </div>
              <Table
                columns={deviceColumns || []}
                visibleColumns={deviceVisibleColumns}
                tableId="stock-devices-list"
                loading={loading}
                search={true}
                externalSearch={true}
                onSearch={(value) => {
                  setSearchQuery(value);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                searchPlaceholder="Seri numarası ile ara..."
                externalSorting={true}
                onSort={(nextSort) => {
                  setSort(nextSort);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                  setSelectedDevices(new Set());
                }}
                hideFooter={true}
                viewOptions={
                  <ListViewOptionsPanel
                    columns={deviceColumns}
                    visibleColumns={deviceVisibleColumns}
                    onToggleColumn={toggleDeviceColumn}
                    onResetColumns={resetDeviceColumns}
                    showApply={false}
                    showReset={false}
                  />
                }
              >
                {(devices || []).map((device) => (
                  <Tr key={device._id} data={device}>
                    {deviceVisibleColumns.includes(-1) && (
                      <Td>
                        <label className="device-checkbox-label">
                          <input
                            type="checkbox"
                            className="device-checkbox"
                            checked={selectedDevices.has(device._id)}
                            onChange={() => toggleDeviceSelection(device._id)}
                          />
                          <span className="custom-checkbox">
                            {selectedDevices.has(device._id) && (
                              <FiCheck className="check-icon" />
                            )}
                          </span>
                        </label>
                      </Td>
                    )}
                    {deviceVisibleColumns.includes(0) && (
                      <Td>{device.serialNumber}</Td>
                    )}
                    {deviceVisibleColumns.includes(2) && (
                      <Td>
                        <div
                          style={{
                            backgroundColor: getStatusAppearance(device.status)
                              .bg,
                            color: getStatusAppearance(device.status).text,
                            padding: "5px 10px",
                            borderRadius: "5px",
                            width: "fit-content",
                          }}
                        >
                          {getStatusLabel(device.status)}
                        </div>
                      </Td>
                    )}
                    {deviceVisibleColumns.includes(3) && (
                      <Td>{formatInstantDateShortTr(device.createdAt)}</Td>
                    )}
                  </Tr>
                ))}
              </Table>

              {/* Pagination */}
              {!loading && pagination.total > 0 && (
                <div className="mt-3">
                  <Pagination
                    totalItems={pagination.total}
                    totalPerPage={pagination.limit}
                    currentPage={pagination.page}
                    totalRecords={pagination.total}
                    onPageChange={({ pagination: paginationData }) => {
                      setPagination((prev) => {
                        const limitChanged =
                          prev.limit !== paginationData.itemsPerPage;
                        return {
                          ...prev,
                          page: limitChanged ? 1 : paginationData.currentPage,
                          limit: paginationData.itemsPerPage,
                        };
                      });
                    }}
                    options={{
                      skipPrevious: true,
                      previous: true,
                      next: true,
                      skipNext: true,
                      selectQuantity: true,
                      simpleCounting: false,
                    }}
                  />
                </div>
              )}
            </>
          )}
        </ContainerItem>
      </Container>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>

      <style>
        {`
          .stock-products-select-all {
            padding: 0.875rem 1rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
            border: 1px solid #e9ecef;
            border-radius: 10px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
          }

          .select-all-label {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            cursor: pointer;
            user-select: none;
            margin: 0;
          }

          .select-all-checkbox,
          .device-checkbox {
            display: none;
          }

          .custom-checkbox {
            width: 20px;
            height: 20px;
            border-radius: 6px;
            border: 2px solid #cfd4dc;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            background: #fff;
            flex-shrink: 0;
            position: relative;
          }

          .select-all-checkbox:checked + .custom-checkbox,
          .device-checkbox:checked + .custom-checkbox {
            background: #00b4f3;
            border-color: #00b4f3;
            box-shadow: 0 2px 8px rgba(0, 151, 178, 0.25);
          }

          .select-all-checkbox:not(:checked) + .custom-checkbox:hover,
          .device-checkbox:not(:checked) + .custom-checkbox:hover {
            border-color: #00b4f3;
            background: #f0f9ff;
          }

          .check-icon {
            color: #fff;
            font-size: 14px;
            stroke-width: 3;
            pointer-events: none;
          }

          .select-all-text {
            font-weight: 600;
            color: #1a202c;
            font-size: 14px;
          }

          .selection-count {
            color: #00b4f3;
            font-weight: 700;
          }

          .device-checkbox-label {
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            user-select: none;
            margin: 0;
            padding: 0.25rem;
          }

          .device-checkbox-label:hover .custom-checkbox {
            border-color: #00b4f3;
            background: #f0f9ff;
          }

          .device-checkbox:checked + .custom-checkbox {
            background: #00b4f3;
            border-color: #00b4f3;
            box-shadow: 0 2px 6px rgba(0, 151, 178, 0.2);
          }

          @media (max-width: 768px) {
            .stock-products-select-all {
              padding: 0.75rem;
            }

            .select-all-text {
              font-size: 13px;
            }

            .custom-checkbox {
              width: 18px;
              height: 18px;
            }

            .check-icon {
              font-size: 12px;
            }
          }
        `}
      </style>
    </AppPage>
  );
};

export default withAuth(StockProducts, ["admin", "employee", "user"]);
